/**
 * Creating a project, and the one time its link is legible.
 *
 * This is where Cloud's "New Scope project" menu item lands. Creation mints
 * the link in the same request, so the success state is the link — shown once,
 * copyable, and stated plainly as being shown once.
 */

import { useState } from 'react';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { api, ApiError } from '../api';
import { RADIUS, accountOrigin, display, displayLead, formatDate, mono, panel } from '../theme';
import { useApp } from '../state';
import {
  emptySettings,
  forWire,
  InterviewSettingsFields
} from '../components/InterviewSettingsFields';

export function NewProject() {
  const { Button, Icon, Input } = ds();
  const { navigate } = useRouter();
  const { me } = useApp();

  const [title, setTitle] = useState('');
  const [smeName, setSmeName] = useState('');
  const [team, setTeam] = useState('');
  const [meeting, setMeeting] = useState('');
  const [tuning, setTuning] = useState(false);
  const [interview, setInterview] = useState(emptySettings());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ projectId: string; url: string; expiresAt: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const locked = me?.projectsAllowed === 0;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const settings = forWire(interview);
      const res = await api.projects.create({
        title: title.trim(),
        smeName: smeName.trim(),
        ...(team.trim() ? { team: team.trim() } : {}),
        // `datetime-local` has no timezone; the browser's own offset is the
        // honest reading of what the designer typed.
        ...(meeting ? { meetingAt: Math.floor(new Date(meeting).getTime() / 1000) } : {}),
        tone: settings.tone,
        questionGuide: settings.questionGuide
      });
      setMade({
        projectId: res.projectId,
        url: `${window.location.origin}${res.url}`,
        expiresAt: res.expiresAt
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that project.');
    } finally {
      setBusy(false);
    }
  };

  if (locked) {
    return (
      <Frame>
        <h1 style={display(36)}>Scope is part of Pro.</h1>
        <p style={{ ...displayLead(20), margin: '12px 0 24px' }}>
          Move up to send your first intake link.
        </p>
        <Button variant="primary" size="md" onClick={() => window.location.assign(`${accountOrigin()}/pricing`)}>
          See the plans
        </Button>
      </Frame>
    );
  }

  if (made) {
    return (
      <Frame>
        <div style={{ ...mono, marginBottom: 14 }}>Ready to send</div>
        <h1 style={display(36)}>Send this to your expert.</h1>
        <p style={{ ...displayLead(20), margin: '12px 0 24px' }}>
          This link is shown once and works until {formatDate(made.expiresAt)}. You can make a
          new one later.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: '14px 16px',
            background: 'var(--surface-inset)',
            borderRadius: RADIUS.control,
            marginBottom: 24,
            flexWrap: 'wrap'
          }}
        >
          <code
            style={{
              fontFamily: "var(--font-mono), monospace",
              fontSize: 13,
              color: 'var(--text-body)',
              wordBreak: 'break-all',
              flex: '1 1 260px'
            }}
          >
            {made.url}
          </code>
          <Button
            variant="soft"
            size="sm"
            onClick={() => {
              void navigator.clipboard?.writeText(made.url);
              setCopied(true);
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="primary" size="md" onClick={() => navigate(`/p/${made.projectId}`)}>
            Open the project
          </Button>
          <Link to="/">
            <Button variant="ghost" size="md">
              All submissions
            </Button>
          </Link>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div style={{ ...mono, marginBottom: 14 }}>New project</div>
      <h1 style={display(36)}>Scope a new request.</h1>
      <p style={{ ...displayLead(20), margin: '12px 0 28px' }}>
        Name the request and who knows about it. You get a link to send them.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          label="What is the request?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Expense claim errors"
        />
        <Input
          label="Who knows about it?"
          value={smeName}
          onChange={(e) => setSmeName(e.target.value)}
          placeholder="Priya Raman"
        />
        <Input
          label="Their team or area"
          value={team}
          onChange={(e) => setTeam(e.target.value)}
          placeholder="Finance operations"
        />
        <Input
          label="Scope meeting"
          type="datetime-local"
          value={meeting}
          onChange={(e) => setMeeting(e.target.value)}
        />

        {/* Closed by default: the four fields above are enough to send a link,
            and every project that does not need steering should not have to
            read about it. */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 18 }}>
          <button
            type="button"
            onClick={() => setTuning((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'transparent',
              border: 0,
              padding: 0,
              color: 'var(--text-body)',
              fontSize: 14
            }}
          >
            <Icon name={tuning ? 'chevron-down' : 'arrow-right'} size={14} />
            How the interview runs
          </button>

          {tuning && (
            <div style={{ marginTop: 22 }}>
              <InterviewSettingsFields value={interview} onChange={setInterview} />
            </div>
          )}
        </div>

        {error && <p style={{ margin: 0, fontSize: 14, color: 'var(--danger-500)' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Button
            variant="primary"
            size="md"
            disabled={!title.trim() || !smeName.trim() || busy}
            onClick={() => void create()}
          >
            Create project
          </Button>
          <Link to="/">
            <Button variant="ghost" size="md">
              Cancel
            </Button>
          </Link>
        </div>
      </div>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 48px 140px' }}>
      <div style={{ ...panel, padding: '40px 44px', maxWidth: 620 }}>{children}</div>
    </div>
  );
}
